(function () {
  "use strict";

  var desktopQuery = window.matchMedia("(min-width: 990px)");
  var reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

  document.documentElement.classList.remove("no-js");
  document.documentElement.classList.add("js");

  // 2560px covers a full-width image on a 1280px-wide retina screen; larger
  // only adds download weight. Images with their own srcset are left alone.
  var maximumImageWidth = "2560";
  var maximumImagePath = /\/cdn\/shop\/(?:files|articles|products|collections)\//i;

  function maximumResolutionImageUrl(source) {
    if (!source) return null;

    try {
      var url = new URL(source, document.baseURI);
      if (!maximumImagePath.test(url.pathname)) return null;
      url.searchParams.set("width", maximumImageWidth);
      return url.href;
    } catch (error) {
      return null;
    }
  }

  function upgradeImageToMaximumResolution(image) {
    if (!image || image.tagName !== "IMG") return;
    if (image.hasAttribute("data-ynt-responsive-image")) return;
    if (image.hasAttribute("srcset")) return;

    var source = image.getAttribute("src") || image.currentSrc;
    var fullSource = maximumResolutionImageUrl(source);
    if (!fullSource) return;

    if (image.src !== fullSource) image.src = fullSource;
  }

  function upgradeImagesToMaximumResolution(root) {
    if (!root || root.nodeType !== 1) return;
    if (root.tagName === "IMG") upgradeImageToMaximumResolution(root);
    root.querySelectorAll("img").forEach(upgradeImageToMaximumResolution);
  }

  upgradeImagesToMaximumResolution(document.documentElement);

  var maximumImageObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.type === "attributes") {
        upgradeImageToMaximumResolution(mutation.target);
        return;
      }

      mutation.addedNodes.forEach(function (node) {
        upgradeImagesToMaximumResolution(node);
      });
    });
  });

  maximumImageObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["src", "srcset", "sizes"],
    childList: true,
    subtree: true
  });

  function setLinkState(link, isActive) {
    if (!link) return;

    if (isActive) {
      link.removeAttribute("aria-hidden");
      link.removeAttribute("tabindex");
      link.removeAttribute("inert");
      return;
    }

    link.setAttribute("aria-hidden", "true");
    link.setAttribute("tabindex", "-1");
    link.setAttribute("inert", "");
  }

  function enhanceMobileMenu(details) {
    if (!details || details.dataset.siteMenuReady === "true") return function () {};

    details.dataset.siteMenuReady = "true";
    var summary = details.querySelector("summary");
    var pointerToggled = false;

    function closeMenu() {
      if (!details.open) return;
      details.open = false;
    }

    function handleKeydown(event) {
      if (event.key !== "Escape" || !details.open) return;
      closeMenu();
      if (summary) summary.focus();
    }

    function handleDocumentClick(event) {
      if (details.open && !details.contains(event.target)) closeMenu();
    }

    function handleLinkClick(event) {
      if (event.target.closest("a")) closeMenu();
    }

    function handleSummaryPointerDown() {
      pointerToggled = true;
    }

    function handleToggle() {
      if (details.open) {
        pointerToggled = false;
        return;
      }

      if (!pointerToggled || !summary) return;
      pointerToggled = false;
      window.requestAnimationFrame(function () {
        summary.blur();
      });
    }

    details.addEventListener("click", handleLinkClick);
    details.addEventListener("toggle", handleToggle);
    if (summary) summary.addEventListener("pointerdown", handleSummaryPointerDown);
    document.addEventListener("keydown", handleKeydown);
    document.addEventListener("click", handleDocumentClick);

    return function () {
      details.removeEventListener("click", handleLinkClick);
      details.removeEventListener("toggle", handleToggle);
      if (summary) summary.removeEventListener("pointerdown", handleSummaryPointerDown);
      document.removeEventListener("keydown", handleKeydown);
      document.removeEventListener("click", handleDocumentClick);
      delete details.dataset.siteMenuReady;
    };
  }

  function enhanceSidecart(root, mobileMenu) {
    if (!root || root.dataset.siteSidecartReady === "true") return function () {};

    root.dataset.siteSidecartReady = "true";
    var mobileDrawerQuery = window.matchMedia("(max-width: 989px)");
    var triggers = Array.prototype.slice.call(document.querySelectorAll("[data-sidecart-open]"));
    var closeButtons = Array.prototype.slice.call(root.querySelectorAll("[data-sidecart-close]"));
    var closeButton = root.querySelector(".site-sidecart__close");
    var itemsContainer = root.querySelector("[data-sidecart-items]");
    var footer = root.querySelector("[data-sidecart-footer]");
    var total = root.querySelector("[data-sidecart-total]");
    var status = root.querySelector("[data-sidecart-status]");
    var message = root.querySelector("[data-sidecart-message]");
    var reviewLink = root.querySelector(".site-sidecart__checkout");
    var cartJsonUrl = root.dataset.cartJsonUrl;
    var cartChangeUrl = root.dataset.cartChangeUrl;
    var cartUrl = root.dataset.cartUrl;
    var lastFocused = null;
    var cartRequest = null;
    var mutationPending = false;
    var cartLoaded = false;
    var currentCart = null;
    var destroyed = false;
    var inertTargets = [document.querySelector("main"), document.querySelector(".site-footer")].filter(Boolean);
    var inertStates = [];

    function formatMoney(cents, currency) {
      var activeCurrency = currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "GBP";
      try {
        return new Intl.NumberFormat(document.documentElement.lang || "en-GB", {
          style: "currency",
          currency: activeCurrency
        }).format((Number(cents) || 0) / 100);
      } catch (error) {
        return ((Number(cents) || 0) / 100).toFixed(2) + " " + activeCurrency;
      }
    }

    function setStatus(message) {
      if (status) status.textContent = message || "";
    }

    function clearMessage() {
      if (!message) return;
      message.hidden = true;
      message.textContent = "";
    }

    function showError(messageText) {
      if (message) {
        message.textContent = messageText;
        message.hidden = false;
      }
      setStatus(messageText);
    }

    function setCartCount(count) {
      var safeCount = Number(count) || 0;
      var displayCount = safeCount > 99 ? "99+" : String(safeCount);
      document.querySelectorAll("[data-cart-count]").forEach(function (countElement) {
        countElement.textContent = displayCount;
        countElement.hidden = safeCount === 0;
      });
      triggers.forEach(function (trigger) {
        trigger.hidden = safeCount === 0;
        trigger.setAttribute(
          "aria-label",
          "Open cart, " + safeCount + " item" + (safeCount === 1 ? "" : "s")
        );
      });
    }

    function createElement(tagName, className, textValue) {
      var element = document.createElement(tagName);
      if (className) element.className = className;
      if (textValue !== undefined && textValue !== null) element.textContent = textValue;
      return element;
    }

    function createQuantityButton(label, symbol, action, item, quantity) {
      var button = createElement("button", "no-hover", symbol);
      button.type = "button";
      button.dataset.cartAction = action;
      button.dataset.cartKey = item.key;
      button.dataset.cartQuantity = String(quantity);
      button.setAttribute("aria-label", label + " " + (item.product_title || item.title || "item"));
      return button;
    }

    function createCartItem(item, currency) {
      var article = createElement("article", "site-sidecart__item");
      article.dataset.cartItemKey = item.key;

      var media;
      if (item.image) {
        media = createElement("a", "site-sidecart__item-media");
        media.href = item.url || cartUrl;
        var image = document.createElement("img");
        image.src = item.image;
        image.alt = item.product_title || item.title || "Cart item";
        image.width = 168;
        image.height = 168;
        image.loading = "lazy";
        media.appendChild(image);
      } else {
        media = createElement("div", "site-sidecart__item-media site-sidecart__item-media--placeholder");
        media.setAttribute("aria-hidden", "true");
        media.appendChild(createElement("span", "", "YNT"));
      }

      var content = createElement("div", "site-sidecart__item-content");
      var title = createElement(
        "a",
        "site-sidecart__item-title",
        item.product_title || item.title || "Cart item"
      );
      title.href = item.url || cartUrl;
      content.appendChild(title);

      var metaParts = [];
      if (item.variant_title && item.variant_title !== "Default Title") metaParts.push(item.variant_title);
      if (
        item.selling_plan_allocation &&
        item.selling_plan_allocation.selling_plan &&
        item.selling_plan_allocation.selling_plan.name
      ) {
        metaParts.push(item.selling_plan_allocation.selling_plan.name);
      }
      if (item.properties) {
        Object.keys(item.properties).forEach(function (propertyName) {
          var propertyValue = item.properties[propertyName];
          if (
            propertyName.charAt(0) !== "_" &&
            propertyValue !== null &&
            propertyValue !== undefined &&
            String(propertyValue).trim() !== ""
          ) {
            metaParts.push(propertyName + ": " + String(propertyValue));
          }
        });
      }
      if (metaParts.length) content.appendChild(createElement("p", "site-sidecart__item-meta", metaParts.join(" · ")));

      content.appendChild(
        createElement("p", "site-sidecart__item-price", formatMoney(item.final_line_price, currency))
      );

      var actions = createElement("div", "site-sidecart__item-actions");
      var quantity = createElement("div", "site-sidecart__quantity");
      quantity.setAttribute("aria-label", "Quantity for " + (item.product_title || item.title || "item"));
      quantity.appendChild(
        createQuantityButton("Decrease quantity for", "−", "decrease", item, Math.max(0, item.quantity - 1))
      );
      var quantityValue = createElement("span", "", String(item.quantity));
      quantityValue.setAttribute("aria-live", "polite");
      quantity.appendChild(quantityValue);
      quantity.appendChild(
        createQuantityButton("Increase quantity for", "+", "increase", item, item.quantity + 1)
      );
      actions.appendChild(quantity);

      var remove = createElement("button", "site-sidecart__remove no-hover", "Remove");
      remove.type = "button";
      remove.dataset.cartAction = "remove";
      remove.dataset.cartKey = item.key;
      remove.dataset.cartQuantity = "0";
      remove.setAttribute("aria-label", "Remove " + (item.product_title || item.title || "item") + " from cart");
      actions.appendChild(remove);
      content.appendChild(actions);

      article.appendChild(media);
      article.appendChild(content);
      return article;
    }

    function restoreMutationFocus(focusRequest) {
      if (!focusRequest) return;
      window.requestAnimationFrame(function () {
        var candidates = Array.prototype.slice.call(root.querySelectorAll("[data-cart-action]"));
        var nextFocus = candidates.find(function (candidate) {
          return (
            candidate.dataset.cartKey === focusRequest.key &&
            candidate.dataset.cartAction === focusRequest.action
          );
        });
        if (!nextFocus) nextFocus = candidates[0] || closeButton;
        if (nextFocus) nextFocus.focus();
      });
    }

    function renderCart(cart, focusRequest) {
      if (destroyed || !itemsContainer) return;
      currentCart = cart;
      cartLoaded = true;
      itemsContainer.removeAttribute("aria-busy");
      itemsContainer.replaceChildren();
      setCartCount(cart.item_count);
      if (total) total.textContent = formatMoney(cart.total_price, cart.currency);

      if (!cart.items || !cart.items.length) {
        var empty = createElement("div", "site-sidecart__empty");
        var emptyCopy = createElement("div");
        emptyCopy.appendChild(createElement("strong", "", "Your cart is empty."));
        emptyCopy.appendChild(
          createElement("p", "", "When something catches your eye, it will appear here ready to review.")
        );
        empty.appendChild(emptyCopy);
        itemsContainer.appendChild(empty);
        if (footer) footer.hidden = true;
        setStatus("Your cart is empty");
        restoreMutationFocus(focusRequest);
        return;
      }

      var fragment = document.createDocumentFragment();
      cart.items.forEach(function (item) {
        fragment.appendChild(createCartItem(item, cart.currency));
      });
      itemsContainer.appendChild(fragment);
      if (footer) footer.hidden = false;
      setStatus(cart.item_count + " item" + (cart.item_count === 1 ? "" : "s") + " in your cart");
      restoreMutationFocus(focusRequest);
    }

    function renderInitialError() {
      if (!itemsContainer || cartLoaded) return;
      itemsContainer.removeAttribute("aria-busy");
      itemsContainer.replaceChildren();
      var errorState = createElement("div", "site-sidecart__empty");
      var errorCopy = createElement("div");
      errorCopy.appendChild(createElement("strong", "", "We couldn’t load the cart."));
      errorCopy.appendChild(createElement("p", "", "Try again, or open the full cart page."));
      var retry = createElement("button", "site-sidecart__remove no-hover", "Try again");
      retry.type = "button";
      retry.dataset.sidecartRetry = "true";
      errorCopy.appendChild(retry);
      var fallback = createElement("a", "site-sidecart__checkout", "Open full cart");
      fallback.href = cartUrl;
      errorCopy.appendChild(fallback);
      errorState.appendChild(errorCopy);
      itemsContainer.appendChild(errorState);
      if (footer) footer.hidden = true;
    }

    function fetchCart(showLoading, preserveMessage) {
      if (!cartJsonUrl || destroyed) return Promise.resolve();
      if (mutationPending) return Promise.resolve(currentCart);
      if (cartRequest) cartRequest.abort();
      cartRequest = typeof AbortController !== "undefined" ? new AbortController() : null;
      if (!preserveMessage) clearMessage();
      if (itemsContainer) itemsContainer.setAttribute("aria-busy", "true");
      if (showLoading && !cartLoaded && itemsContainer) {
        itemsContainer.innerHTML = '<div class="site-sidecart__loading" aria-hidden="true"></div><p>Loading your cart…</p>';
      }
      if (!preserveMessage) setStatus("Loading cart");

      return fetch(cartJsonUrl, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        signal: cartRequest ? cartRequest.signal : undefined
      })
        .then(function (response) {
          if (!response.ok) throw new Error("Cart request failed");
          return response.json();
        })
        .then(function (cart) {
          if (!destroyed) renderCart(cart);
          return cart;
        })
        .catch(function (error) {
          if (error && error.name === "AbortError") return;
          if (itemsContainer) itemsContainer.removeAttribute("aria-busy");
          showError("We couldn’t load the cart. Please try again.");
          renderInitialError();
        });
    }

    function setMutationBusy(isBusy) {
      mutationPending = isBusy;
      if (itemsContainer) {
        itemsContainer.toggleAttribute("aria-busy", isBusy);
        itemsContainer.querySelectorAll("button").forEach(function (button) {
          button.disabled = isBusy;
        });
      }
      if (reviewLink) {
        reviewLink.setAttribute("aria-disabled", isBusy ? "true" : "false");
        if (isBusy) {
          reviewLink.setAttribute("tabindex", "-1");
        } else {
          reviewLink.removeAttribute("aria-disabled");
          reviewLink.removeAttribute("tabindex");
        }
      }
    }

    function changeLine(key, quantity, focusRequest) {
      if (mutationPending || !cartChangeUrl) return;
      if (cartRequest) {
        cartRequest.abort();
        cartRequest = null;
      }
      setMutationBusy(true);
      setStatus("Updating cart");
      fetch(cartChangeUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function (response) {
          return response.json().then(function (payload) {
            if (!response.ok) throw new Error(payload.description || "Cart update failed");
            return payload;
          });
        })
        .then(function (cart) {
          if (destroyed) return;
          setMutationBusy(false);
          clearMessage();
          renderCart(cart, focusRequest);
          document.dispatchEvent(
            new CustomEvent("ynt:cart-updated", { detail: { cart: cart, source: "sidecart" } })
          );
        })
        .catch(function (error) {
          if (destroyed) return;
          setMutationBusy(false);
          var errorMessage =
            error && error.message && error.message !== "Cart update failed"
              ? error.message
              : "That change didn’t go through. Your cart has been refreshed.";
          showError(errorMessage);
          fetchCart(false, true).then(function () {
            if (!destroyed) showError(errorMessage);
          });
        });
    }

    function setBackgroundInert(isInert) {
      if (isInert) {
        inertStates = inertTargets.map(function (target) {
          var state = { target: target, inert: target.hasAttribute("inert") };
          target.setAttribute("inert", "");
          return state;
        });
        return;
      }
      inertStates.forEach(function (state) {
        if (!state.inert) state.target.removeAttribute("inert");
      });
      inertStates = [];
    }

    function isOpen() {
      return root.classList.contains("is-open");
    }

    function openDrawer(trigger, event) {
      if (!mobileDrawerQuery.matches) return;
      if (event) event.preventDefault();
      if (isOpen()) return;
      if (typeof window.closeThemeModal === "function" && document.querySelector(".theme-modal.reveal")) {
        window.closeThemeModal(true);
      }
      if (mobileMenu) mobileMenu.open = false;
      lastFocused = trigger || document.activeElement;
      root.removeAttribute("inert");
      root.setAttribute("aria-hidden", "false");
      root.classList.add("is-open");
      document.body.classList.add("is-sidecart-open");
      setBackgroundInert(true);
      triggers.forEach(function (cartTrigger) {
        cartTrigger.setAttribute("aria-expanded", "true");
      });
      window.requestAnimationFrame(function () {
        if (closeButton) closeButton.focus();
      });
      fetchCart(true);
    }

    function closeDrawer(restoreFocus) {
      if (!isOpen()) return;
      root.classList.remove("is-open");
      document.body.classList.remove("is-sidecart-open");
      triggers.forEach(function (cartTrigger) {
        cartTrigger.setAttribute("aria-expanded", "false");
      });
      setBackgroundInert(false);
      var returnTarget = lastFocused && document.contains(lastFocused) ? lastFocused : triggers[0];
      if (restoreFocus !== false && returnTarget) returnTarget.focus();
      root.setAttribute("aria-hidden", "true");
      root.setAttribute("inert", "");
    }

    function getFocusableElements() {
      return Array.prototype.slice
        .call(root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter(function (element) {
          return (
            element.tabIndex >= 0 &&
            !element.classList.contains("site-sidecart__backdrop") &&
            element.getClientRects().length > 0 &&
            !element.closest("[inert]")
          );
        });
    }

    function handleDocumentKeydown(event) {
      if (!isOpen()) return;
      if (event.key === "Escape") {
        event.preventDefault();
        closeDrawer(true);
        return;
      }
      if (event.key !== "Tab") return;
      var focusable = getFocusableElements();
      if (!focusable.length) {
        event.preventDefault();
        if (closeButton) closeButton.focus();
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function handleDocumentFocus(event) {
      if (isOpen() && !root.contains(event.target) && closeButton) closeButton.focus();
    }

    function handleItemsClick(event) {
      var retry = event.target.closest("[data-sidecart-retry]");
      if (retry) {
        clearMessage();
        fetchCart(true, false);
        return;
      }
      var control = event.target.closest("[data-cart-action]");
      if (!control) return;
      changeLine(control.dataset.cartKey, Number(control.dataset.cartQuantity), {
        key: control.dataset.cartKey,
        action: control.dataset.cartAction
      });
    }

    function handleViewportChange(event) {
      if (!event.matches) closeDrawer(false);
    }

    function handleWindowFocus() {
      if (isOpen()) fetchCart(false);
    }

    function handleTriggerClick(event) {
      openDrawer(event.currentTarget, event);
    }

    function handleCloseClick() {
      closeDrawer(true);
    }

    function handleExternalCartUpdate(event) {
      if (event.detail && event.detail.source === "sidecart") return;
      if (event.detail && event.detail.cart) {
        setCartCount(event.detail.cart.item_count);
        if (isOpen()) renderCart(event.detail.cart);
      } else if (isOpen()) {
        fetchCart(false);
      }
    }

    function handleReviewClick(event) {
      if (mutationPending) event.preventDefault();
    }

    triggers.forEach(function (trigger) {
      trigger.addEventListener("click", handleTriggerClick);
    });
    closeButtons.forEach(function (button) {
      button.addEventListener("click", handleCloseClick);
    });
    if (itemsContainer) itemsContainer.addEventListener("click", handleItemsClick);
    if (reviewLink) reviewLink.addEventListener("click", handleReviewClick);
    document.addEventListener("keydown", handleDocumentKeydown);
    document.addEventListener("focusin", handleDocumentFocus);
    document.addEventListener("cart:updated", handleExternalCartUpdate);
    document.addEventListener("cart:refresh", handleExternalCartUpdate);
    document.addEventListener("ynt:cart-updated", handleExternalCartUpdate);
    window.addEventListener("focus", handleWindowFocus);
    if (mobileDrawerQuery.addEventListener) {
      mobileDrawerQuery.addEventListener("change", handleViewportChange);
    } else {
      mobileDrawerQuery.addListener(handleViewportChange);
    }

    return function () {
      destroyed = true;
      if (cartRequest) cartRequest.abort();
      closeDrawer(false);
      triggers.forEach(function (trigger) {
        trigger.removeEventListener("click", handleTriggerClick);
      });
      closeButtons.forEach(function (button) {
        button.removeEventListener("click", handleCloseClick);
      });
      if (itemsContainer) itemsContainer.removeEventListener("click", handleItemsClick);
      if (reviewLink) reviewLink.removeEventListener("click", handleReviewClick);
      document.removeEventListener("keydown", handleDocumentKeydown);
      document.removeEventListener("focusin", handleDocumentFocus);
      document.removeEventListener("cart:updated", handleExternalCartUpdate);
      document.removeEventListener("cart:refresh", handleExternalCartUpdate);
      document.removeEventListener("ynt:cart-updated", handleExternalCartUpdate);
      window.removeEventListener("focus", handleWindowFocus);
      if (mobileDrawerQuery.removeEventListener) {
        mobileDrawerQuery.removeEventListener("change", handleViewportChange);
      } else {
        mobileDrawerQuery.removeListener(handleViewportChange);
      }
      delete root.dataset.siteSidecartReady;
    };
  }

  function initialise() {
    if (window.__yntSiteNavigationCleanup) window.__yntSiteNavigationCleanup();

    var body = document.body;
    var headerShell = document.querySelector("[data-site-header]");
    var headerContact = document.querySelector("[data-contact-control]");
    var footerDock = document.querySelector("[data-contact-dock]");
    var footerContact = document.querySelector("[data-contact-footer]");
    var mobileMenu = document.querySelector("[data-site-mobile-menu]");
    var cleanMobileMenu = enhanceMobileMenu(mobileMenu);
    var sidecart = document.querySelector("[data-site-sidecart]");
    var cleanSidecart = enhanceSidecart(sidecart, mobileMenu);
    var pendingState = null;
    var focusWaitTarget = null;
    var scrollFrame = null;
    var headerScrollY = Math.max(0, window.scrollY);
    var headerScrollDirection = 0;
    var headerDirectionStartY = headerScrollY;
    var headerCondensed = headerScrollY >= 72;

    function updateHeaderScrollState() {
      if (!headerShell) return;

      var currentScrollY = Math.max(0, window.scrollY);
      var scrollDelta = currentScrollY - headerScrollY;

      if (currentScrollY <= 40) {
        headerCondensed = false;
        headerScrollDirection = 0;
        headerDirectionStartY = currentScrollY;
      } else if (scrollDelta !== 0) {
        var nextDirection = scrollDelta > 0 ? 1 : -1;

        if (nextDirection !== headerScrollDirection) {
          headerScrollDirection = nextDirection;
          headerDirectionStartY = headerScrollY;
        }

        var directionTravel = Math.abs(currentScrollY - headerDirectionStartY);
        if (nextDirection < 0 && directionTravel >= 12) {
          headerCondensed = false;
        } else if (nextDirection > 0 && currentScrollY >= 72 && directionTravel >= 12) {
          headerCondensed = true;
        }
      }

      headerScrollY = currentScrollY;
      headerShell.classList.toggle("is-scrolled", headerCondensed);
    }

    function clamp(value, minimum, maximum) {
      return Math.min(maximum, Math.max(minimum, value));
    }

    function resetContactTravel() {
      body.classList.remove("is-contact-travelling");
      if (!headerContact) return;
      headerContact.style.removeProperty("--contact-travel-x");
      headerContact.style.removeProperty("--contact-travel-y");
      headerContact.style.removeProperty("--contact-travel-scale");
      headerContact.style.removeProperty("--contact-travel-progress");
    }

    function updateContactTravelState() {
      if (!headerContact || !footerDock || !footerContact) return;

      if (!desktopQuery.matches || !body.classList.contains("site-contact-enhanced")) {
        resetContactTravel();
        return;
      }

      var documentElement = document.documentElement;
      var scrollMaximum = Math.max(0, documentElement.scrollHeight - window.innerHeight);
      var travelDistance = clamp(window.innerHeight * 0.5, 480, 680);
      var travelStart = Math.max(0, scrollMaximum - travelDistance);
      var rawProgress = clamp(
        (window.scrollY - travelStart) / Math.max(1, scrollMaximum - travelStart),
        0,
        1
      );
      var easedProgress = reducedMotionQuery.matches
        ? 0
        : rawProgress * rawProgress * (3 - 2 * rawProgress);

      var offsetParent = headerContact.offsetParent;
      var parentRect = offsetParent
        ? offsetParent.getBoundingClientRect()
        : { left: 0, top: 0 };
      var originLeft = parentRect.left + headerContact.offsetLeft;
      var originTop = parentRect.top + headerContact.offsetTop;
      var originWidth = headerContact.offsetWidth;
      var originCenterX = originLeft + originWidth / 2;
      var originCenterY = originTop + headerContact.offsetHeight / 2;

      var dockRect = footerDock.getBoundingClientRect();
      var targetWidth = footerContact.offsetWidth;
      var targetCenterX = dockRect.left + dockRect.width / 2;
      var targetDocumentCenterY = dockRect.top + window.scrollY + targetWidth / 2;
      var targetCenterY = targetDocumentCenterY - scrollMaximum;
      var targetScale = targetWidth / Math.max(1, originWidth);

      var translateX = (targetCenterX - originCenterX) * easedProgress;
      var translateY = (targetCenterY - originCenterY) * easedProgress;
      var scale = 1 + (targetScale - 1) * easedProgress;

      headerContact.style.setProperty("--contact-travel-x", translateX.toFixed(3) + "px");
      headerContact.style.setProperty("--contact-travel-y", translateY.toFixed(3) + "px");
      headerContact.style.setProperty("--contact-travel-scale", scale.toFixed(4));
      headerContact.style.setProperty("--contact-travel-progress", rawProgress.toFixed(4));
      body.classList.toggle(
        "is-contact-travelling",
        rawProgress > 0.002 && rawProgress < 0.995
      );

      var isDocked = body.classList.contains("is-contact-docked");
      var shouldDock = isDocked ? rawProgress >= 0.96 : rawProgress >= 0.995;

      if (shouldDock === isDocked) {
        if (pendingState !== null && pendingState !== shouldDock) {
          pendingState = null;
          removeFocusWait();
        }
      } else if (pendingState !== shouldDock) {
        applyDockState(shouldDock, false);
      }
    }

    function updateScrollState() {
      scrollFrame = null;
      updateHeaderScrollState();
      updateContactTravelState();
    }

    function handleScroll() {
      if (scrollFrame !== null) return;
      scrollFrame = window.requestAnimationFrame(updateScrollState);
    }

    function removeFocusWait() {
      if (!focusWaitTarget) return;
      focusWaitTarget.removeEventListener("blur", applyPendingState);
      focusWaitTarget = null;
    }

    function applyPendingState() {
      removeFocusWait();
      if (pendingState !== null) applyDockState(pendingState, true);
    }

    function applyDockState(isDocked, force) {
      if (!headerContact || !footerContact) return;

      var outgoing = isDocked ? headerContact : footerContact;
      if (!force && document.activeElement === outgoing) {
        pendingState = isDocked;
        removeFocusWait();
        focusWaitTarget = outgoing;
        focusWaitTarget.addEventListener("blur", applyPendingState, { once: true });
        return;
      }

      pendingState = null;
      removeFocusWait();
      body.classList.toggle("is-contact-docked", isDocked);
      setLinkState(headerContact, !isDocked);
      setLinkState(footerContact, isDocked);
    }

    function configureContactHandoff() {
      resetContactTravel();
      body.classList.remove(
        "site-contact-enhanced",
        "is-contact-docked",
        "is-contact-travelling"
      );

      if (!headerContact || !footerDock || !footerContact) return;

      if (!desktopQuery.matches) {
        setLinkState(headerContact, false);
        setLinkState(footerContact, true);
        return;
      }

      if (window.Shopify && window.Shopify.designMode) {
        setLinkState(headerContact, true);
        setLinkState(footerContact, true);
        return;
      }

      body.classList.add("site-contact-enhanced");
      applyDockState(false, true);
      updateContactTravelState();
    }

    function handleViewportChange() {
      configureContactHandoff();
      updateScrollState();
    }

    configureContactHandoff();
    updateScrollState();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });

    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener("change", handleViewportChange);
    } else {
      desktopQuery.addListener(handleViewportChange);
    }

    if (reducedMotionQuery.addEventListener) {
      reducedMotionQuery.addEventListener("change", handleViewportChange);
    } else {
      reducedMotionQuery.addListener(handleViewportChange);
    }

    window.__yntSiteNavigationCleanup = function () {
      cleanSidecart();
      cleanMobileMenu();
      removeFocusWait();
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame);
      scrollFrame = null;
      if (headerShell) headerShell.classList.remove("is-scrolled");
      resetContactTravel();
      body.classList.remove(
        "site-contact-enhanced",
        "is-contact-docked",
        "is-contact-travelling"
      );
      setLinkState(headerContact, true);
      setLinkState(footerContact, true);

      if (desktopQuery.removeEventListener) {
        desktopQuery.removeEventListener("change", handleViewportChange);
      } else {
        desktopQuery.removeListener(handleViewportChange);
      }

      if (reducedMotionQuery.removeEventListener) {
        reducedMotionQuery.removeEventListener("change", handleViewportChange);
      } else {
        reducedMotionQuery.removeListener(handleViewportChange);
      }
    };
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialise, { once: true });
  } else {
    initialise();
  }

  document.addEventListener("shopify:section:load", initialise);
  document.addEventListener("shopify:section:unload", function (event) {
    var target = event.target;
    var ownsNavigation =
      target &&
      ((target.matches && target.matches("[data-site-header], [data-contact-footer]")) ||
        (target.querySelector && target.querySelector("[data-site-header], [data-contact-footer]")));
    if (!ownsNavigation) return;
    if (window.__yntSiteNavigationCleanup) window.__yntSiteNavigationCleanup();
  });
})();
